import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Building2, Gauge, KeyRound, LayoutGrid, Users, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { RedirectToLogin } from "@/components/shell/redirect-to-login";
import { Splash } from "@/components/shell/splash";
import { Wordmark } from "@/components/shell/wordmark";
import { Clients, Plan, Workspaces } from "@/components/studio-console/others";
import { People } from "@/components/studio-console/people";
import { Roles } from "@/components/studio-console/roles";
import type { ConsoleData } from "@/components/studio-console/format";
import { Toaster } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

const TABS = ["people", "roles", "workspaces", "clients", "plan"] as const;
type Tab = (typeof TABS)[number];

const ICONS: Record<Tab, LucideIcon> = {
  people: Users,
  roles: KeyRound,
  workspaces: LayoutGrid,
  clients: Building2,
  plan: Gauge,
};

export const Route = createFileRoute("/studio_/console")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab } =>
    TABS.includes(search.tab as Tab) ? { tab: search.tab as Tab } : {},
  component: StudioConsolePage,
});

function StudioConsolePage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <RedirectToLogin />
      </Unauthenticated>
      <Authenticated>
        <Console />
      </Authenticated>
      <Toaster />
    </>
  );
}

function Console() {
  const { t } = useTranslation("studio");
  const { t: tc } = useTranslation();
  const data = useQuery(api.studioConsole.overview);
  if (data === undefined) return <Splash subtitle={tc("status.loading")} />;
  if (data === null) {
    return (
      <main className="mx-auto grid min-h-svh max-w-md content-center gap-4 px-6">
        <Wordmark size="md" />
        <p className="text-sm text-ink-2">{t("console.notAllowed")}</p>
        <Link to="/" className="text-sm text-ink underline">
          {t("console.backHome")}
        </Link>
      </main>
    );
  }
  return <Dashboard data={data} />;
}

/** A working screen, as the brand console: a sidebar, tables and numbers. */
function Dashboard({ data }: { data: ConsoleData }) {
  const { t } = useTranslation("studio");
  const { tab: asked } = Route.useSearch();
  const tab = asked ?? "people";
  const { status, rejectedBecause, name } = data.organization;

  return (
    <div className="flex min-h-svh flex-col bg-paper-2 md:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-line bg-paper md:sticky md:top-0 md:h-svh md:w-56 md:border-b-0 md:border-e">
        <div className="flex h-12 items-center gap-2 border-b border-line px-4">
          <Wordmark size="sm" />
          <span className="text-xs text-ink-3">{t("console.title")}</span>
        </div>
        <nav className="flex gap-1 overflow-x-auto p-2 md:flex-1 md:flex-col md:overflow-visible">
          {TABS.map((name) => {
            const Icon = ICONS[name];
            return (
              <Link
                key={name}
                to="/studio/console"
                search={{ tab: name }}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  tab === name ? "bg-paper-3 font-medium text-ink" : "text-ink-2 hover:bg-paper-2 hover:text-ink",
                )}
              >
                <Icon className="size-4" />
                {t(`nav.${name}`)}
              </Link>
            );
          })}
        </nav>
        <div className="hidden border-t border-line px-4 py-3 md:block">
          <p className="truncate text-sm font-medium text-ink">{name}</p>
          <p className="text-xs text-ink-3">
            {status === "approved" && data.plan
              ? t("console.footer", { count: data.plan.standardCredits })
              : t("console.footerPending")}
          </p>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-5 py-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {status !== "approved" ? (
            <div
              role="status"
              className={cn(
                "mb-5 rounded-md border px-4 py-3 text-sm",
                status === "rejected" ? "border-red-200 bg-red-50 text-red-900" : "border-line bg-paper text-ink-2",
              )}
            >
              {status === "rejected" ? t("console.rejected", { reason: rejectedBecause ?? "" }) : t("console.pending")}
            </div>
          ) : null}
          {tab === "people" ? <People data={data} /> : null}
          {tab === "roles" ? <Roles data={data} /> : null}
          {tab === "workspaces" ? <Workspaces data={data} /> : null}
          {tab === "clients" ? <Clients data={data} /> : null}
          {tab === "plan" ? <Plan data={data} /> : null}
        </div>
      </main>
    </div>
  );
}
